import React, { useState, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import QuickAddModal from '../QuickAddModal';
import { PhoneInput } from '../ui/PhoneInput';
import { SearchableSelect } from '../ui/SearchableSelect';
import { toast } from 'sonner';
import { Upload, FileText, Loader2, ScanLine, Check, CreditCard } from 'lucide-react';
import CreditCostBadge, { CREDIT_FEATURES } from '../CreditCostBadge';
import { triggerCreditUpdate } from '../CreditIndicator';

const CounselorTab = ({ formData, updateDeskField, masterData, fetchMasterData, readOnly = false, api, documents = [], applicationId, onDocumentUploaded, documentTypes = [], currentUser = null, isAgent = false }) => {
  const data = formData.counselor || {};
  const update = (field, value) => {
    if (readOnly) return;
    updateDeskField('counselor', field, value);
  };
  const users = masterData.users || [];
  const countries = masterData.countries || [];
  const leadStatuses = masterData.leadStatuses || [];
  const applicationTypes = masterData.applicationTypes || [];
  
  // Auto-set counselor to current user if not already set
  React.useEffect(() => {
    if (currentUser && !data.counselor_id && !readOnly && users.length > 0) {
      // Find the current user in the users list
      const matchingUser = users.find(u => u.id === currentUser.user_id || u.email === currentUser.email);
      if (matchingUser) {
        update('counselor_id', matchingUser.id);
      }
    }
  }, [currentUser, data.counselor_id, readOnly, users]);

  // Scan feature state
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [scanMode, setScanMode] = useState('upload');
  const [selectedDocTypeId, setSelectedDocTypeId] = useState(''); // Now stores document type ID from master data
  const [selectedExistingDoc, setSelectedExistingDoc] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [lastUploadedFile, setLastUploadedFile] = useState(null);
  const fileInputRef = useRef(null);

  // Helper functions
  const getDocTypeLabel = (type) => {
    switch (type) {
      case 'passport': return 'Passport';
      case 'nic': return 'NIC / National ID';
      case 'resume': return 'Resume / CV';
      default: return type;
    }
  };

  const getExtractionInfo = (docType) => {
    switch (docType) {
      case 'passport': return 'Name, DOB, Gender, Nationality, Passport details';
      case 'nic': return 'Name, DOB, Gender, NIC number, Address details';
      case 'resume': return 'Name, Email, Phone, Education, Work experience';
      default: return '';
    }
  };
  
  // Get extraction type based on document name (for AI extraction)
  // r60 — Added `english_test` branch so IELTS/PTE/LanguageCert/Oxford ELLT/OET
  // score reports get the specialised extraction prompt instead of being
  // misclassified as résumés (which silently dropped the scores).
  const getExtractionType = (docName) => {
    const name = (docName || '').toLowerCase();
    if (name.includes('passport')) return 'passport';
    if (name.includes('nic') || name.includes('national id') || name.includes('cnic') || name.includes('identity')) return 'nic';
    if (
      name.includes('ielts') || name.includes('pte') || name.includes('pearson') ||
      name.includes('toefl') || name.includes('duolingo') ||
      name.includes('oxford') || name.includes('ellt') ||
      name.includes('languagecert') || name.includes('langcert') || name.includes('lang cert') ||
      name.includes('oet') || name.includes('occupational english') ||
      name.includes('english test') || name.includes('english score') ||
      name.includes('language test') || name.includes('trf') || name.includes('test report')
    ) return 'english_test';
    if (name.includes('resume') || name.includes('cv') || name.includes('transcript') || name.includes('degree') || name.includes('certificate') || name.includes('academic')) return 'resume';
    return 'resume'; // Default to resume for general document extraction
  };

  const renderUserOptions = () => users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>);
  const renderCountryOptions = () => countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>);
  const renderLeadStatusOptions = () => leadStatuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>);

  // Section configuration - simplified, document types come from master data
  const sectionConfig = {
    personal: {
      title: 'Personal Information',
      description: 'Upload any document (Passport/NIC/Resume) - ALL relevant fields across ALL sections will be auto-filled'
    },
    id_documents: {
      title: 'ID Documents',
      description: 'Upload Passport or NIC - ALL relevant fields across ALL sections will be auto-filled'
    },
    address: {
      title: 'Address Details',
      description: 'Upload NIC or Resume - ALL relevant fields across ALL sections will be auto-filled'
    },
    academic: {
      title: 'Academic Details',
      description: 'Upload Resume/CV or Transcript - ALL relevant fields across ALL sections will be auto-filled'
    },
    english_test: {
      title: 'English Test',
      description: 'Upload IELTS/TOEFL score card or Resume - ALL relevant fields across ALL sections will be auto-filled'
    }
  };

  // Open scan dialog for a specific section
  const openScanDialog = (section) => {
    setActiveSection(section);
    setSelectedDocTypeId(documentTypes.length > 0 ? documentTypes[0].id : '');
    setScanMode('upload');
    setSelectedExistingDoc(null);
    setLastUploadedFile(null);
    setScanDialogOpen(true);
  };

  // Save uploaded document to Documents tab with proper document type
  const saveDocumentToApplication = async (file, docTypeId) => {
    if (!file || !api || !applicationId) {
      console.log('saveDocumentToApplication: Missing required params', { file: !!file, api: !!api, applicationId });
      return false;
    }
    
    // Find the document type name
    const docType = documentTypes.find(dt => dt.id === docTypeId);
    const docTypeName = docType?.name || 'Scanned Document';
    
    try {
      console.log('Saving document to application:', { applicationId, docTypeId, docTypeName, fileName: file.name });
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      // Save with document type ID so it appears under the correct heading
      const response = await api().post(
        `/documents/scan-upload?application_id=${applicationId}&document_type_id=${docTypeId || ''}&document_name=${encodeURIComponent(docTypeName)}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      console.log('Document saved successfully:', response.data);
      
      // Notify parent to refresh documents
      if (onDocumentUploaded) {
        onDocumentUploaded();
      }
      
      return true;
    } catch (error) {
      console.error('Failed to save document:', error.response?.data || error.message || error);
      toast.error('Document scanned but failed to save to Documents tab');
      return false;
    }
  };

  // Handle file upload scan
  const handleFileScan = async (file) => {
    if (!file || !api) return;
    
    if (!selectedDocTypeId) {
      toast.error('Please select a document type first');
      return;
    }
    
    setScanning(true);
    setLastUploadedFile(file);
    
    // Create a copy of the file for the second upload (since file streams can only be read once)
    const fileForSave = new File([file], file.name, { type: file.type });
    
    // Get the selected document type and determine extraction type
    const selectedDocType = documentTypes.find(dt => dt.id === selectedDocTypeId);
    // r60 — When scanning from the English Test section, force the
    // english_test prompt even if the doc-type name doesn't match the
    // keyword list (counsellors often tag the file as "Resume" or
    // "Certificate" by mistake).
    const extractionType = activeSection === 'english_test'
      ? 'english_test'
      : getExtractionType(selectedDocType?.name || '');
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api().post(
        `/applications/extract-document?document_type=${extractionType}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        console.log('Extraction successful:', response.data.extracted_data);
        const summary = applyExtractedData(response.data.extracted_data, activeSection);
        triggerCreditUpdate();
        
        // Save document to Documents tab with the selected document type
        const saved = await saveDocumentToApplication(fileForSave, selectedDocTypeId);

        // Surface any audit warnings the backend produced (expired passport,
        // illogical dates, etc.) so the counsellor can act on them.
        const audit = response.data.extracted_data?._field_audit;
        if (audit?.warnings?.length) {
          audit.warnings.forEach((w) => toast.warning(w));
        }

        const docTypeName = selectedDocType?.name || 'Document';
        if (summary && summary.totalFieldsFilled > 0) {
          const sectionsText = summary.filledSections.join(', ');
          if (saved) {
            toast.success(`Filled ${summary.totalFieldsFilled} fields across: ${sectionsText}. Saved as "${docTypeName}" in Documents.`);
          } else {
            toast.success(`Filled ${summary.totalFieldsFilled} fields across: ${sectionsText}.`);
          }
        } else {
          if (saved) {
            toast.success(`Document scanned and saved as "${docTypeName}" in Documents.`);
          } else {
            toast.success(`Document scanned successfully.`);
          }
        }
        setScanDialogOpen(false);
      } else {
        console.error('Extraction failed:', response.data);
        toast.error(response.data.error || 'Failed to extract data');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error(error.response?.data?.detail || 'Failed to scan document');
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle existing document scan
  const handleExistingDocScan = async () => {
    if (!selectedExistingDoc || !api) return;
    
    if (!selectedDocTypeId) {
      toast.error('Please select a document type first');
      return;
    }
    
    setScanning(true);
    try {
      // Download the existing document
      const downloadResponse = await api().get(`/documents/${selectedExistingDoc.id}/download`, {
        responseType: 'blob'
      });
      
      const blob = downloadResponse.data;
      const file = new File([blob], selectedExistingDoc.filename || selectedExistingDoc.file_name || 'document', {
        type: blob.type || 'application/octet-stream'
      });
      
      // Get the selected document type and determine extraction type
      const selectedDocType = documentTypes.find(dt => dt.id === selectedDocTypeId);
      const extractionType = activeSection === 'english_test'
        ? 'english_test'
        : getExtractionType(selectedDocType?.name || '');
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api().post(
        `/applications/extract-document?document_type=${extractionType}`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        console.log('Extraction successful:', response.data.extracted_data);
        const result = applyExtractedData(response.data.extracted_data, activeSection);
        triggerCreditUpdate();
        
        const sectionsSummary = result?.filledSections?.join(', ') || '';
        toast.success(`Filled ${result?.totalFieldsFilled || 0} fields across: ${sectionsSummary}`);
        setScanDialogOpen(false);
      } else {
        console.error('Extraction failed:', response.data);
        toast.error(response.data.error || 'Failed to extract data');
      }
    } catch (error) {
      console.error('Scan error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to scan document';
      toast.error(errorMsg);
    } finally {
      setScanning(false);
    }
  };

  // Apply extracted data to ALL relevant form fields (intelligent fill across all sections)
  const applyExtractedData = (extracted, section) => {
    if (!extracted) return;
    
    let filledSections = [];
    let totalFieldsFilled = 0;

    // === PERSONAL INFORMATION ===
    let personalFields = 0;
    if (extracted.first_name) { update('first_name', extracted.first_name); personalFields++; }
    if (extracted.last_name) { update('last_name', extracted.last_name); personalFields++; }
    if (extracted.full_name && !extracted.first_name && !extracted.last_name) {
      const parts = extracted.full_name.split(' ');
      update('first_name', parts[0] || '');
      update('last_name', parts.slice(1).join(' ') || '');
      personalFields += 2;
    }
    if (extracted.father_name) { update('father_name', extracted.father_name); personalFields++; }
    if (extracted.place_of_birth) { update('place_of_birth', extracted.place_of_birth); personalFields++; }
    if (extracted.date_of_birth) { update('dob', extracted.date_of_birth); personalFields++; }
    if (extracted.gender) { update('gender', extracted.gender.toLowerCase()); personalFields++; }
    if (extracted.email) { update('email', extracted.email); personalFields++; }
    if (extracted.phone) { update('telephone_1', extracted.phone); personalFields++; }
    if (extracted.nationality) {
      const country = countries.find(c => 
        c.name.toLowerCase() === extracted.nationality.toLowerCase() ||
        c.name.toLowerCase().includes(extracted.nationality.toLowerCase())
      );
      if (country) { update('nationality', country.id); personalFields++; }
    }
    if (personalFields > 0) {
      filledSections.push(`Personal Info (${personalFields})`);
      totalFieldsFilled += personalFields;
    }

    // === ID DOCUMENTS ===
    let idFields = 0;
    // Passport
    if (extracted.passport_number) { update('passport', extracted.passport_number); idFields++; }
    if (extracted.passport_issue_date) { update('passport_issue_date', extracted.passport_issue_date); idFields++; }
    if (extracted.passport_expiry_date) { update('passport_expiry_date', extracted.passport_expiry_date); idFields++; }
    // NIC
    if (extracted.nic_number) { update('nic', extracted.nic_number); idFields++; }
    if (extracted.nic_issue_date) { update('nic_issue_date', extracted.nic_issue_date); idFields++; }
    if (extracted.nic_expiry_date) { update('nic_expiry_date', extracted.nic_expiry_date); idFields++; }
    if (idFields > 0) {
      filledSections.push(`ID Documents (${idFields})`);
      totalFieldsFilled += idFields;
    }

    // === ADDRESS DETAILS ===
    let addressFields = 0;
    if (extracted.address) { update('address', extracted.address); addressFields++; }
    if (extracted.city) { update('city', extracted.city); addressFields++; }
    if (extracted.province) { update('province', extracted.province); addressFields++; }
    if (extracted.postal_code) { update('postal_code', extracted.postal_code); addressFields++; }
    if (extracted.country) {
      const country = countries.find(c => 
        c.name.toLowerCase() === extracted.country.toLowerCase() ||
        c.name.toLowerCase().includes(extracted.country.toLowerCase())
      );
      if (country) { update('country_living', country.id); addressFields++; }
    }
    // Also try nationality for country_living if country not found
    if (!extracted.country && extracted.nationality && addressFields === 0) {
      const country = countries.find(c => 
        c.name.toLowerCase() === extracted.nationality.toLowerCase() ||
        c.name.toLowerCase().includes(extracted.nationality.toLowerCase())
      );
      if (country) { update('country_living', country.id); addressFields++; }
    }
    if (addressFields > 0) {
      filledSections.push(`Address (${addressFields})`);
      totalFieldsFilled += addressFields;
    }

    // === ACADEMIC DETAILS ===
    let academicFields = 0;
    if (extracted.highest_qualification) {
      const qualMap = {
        'high school': 'high_school',
        'intermediate': 'high_school',
        'matric': 'high_school',
        'diploma': 'diploma',
        'bachelor': 'bachelors_4',
        'bachelors': 'bachelors_4',
        'bs': 'bachelors_4',
        'bsc': 'bachelors_4',
        'master': 'masters',
        'masters': 'masters',
        'ms': 'masters',
        'msc': 'masters',
        'mcs': 'masters',
        'mba': 'masters',
        'phd': 'phd',
        'doctorate': 'phd',
        'postgraduate': 'masters'
      };
      const qual = Object.entries(qualMap).find(([key]) => 
        extracted.highest_qualification.toLowerCase().includes(key)
      );
      if (qual) { update('last_qualification', qual[1]); academicFields++; }
    }
    if (extracted.institution_name) { update('last_institution', extracted.institution_name); academicFields++; }
    if (extracted.graduation_year) { update('last_qualification_year', extracted.graduation_year); academicFields++; }
    if (extracted.field_of_study) { update('last_qualification_subject', extracted.field_of_study); academicFields++; }
    if (extracted.gpa) { update('last_qualification_grades', extracted.gpa); academicFields++; }
    // Try to set education country from extracted country or city
    if (extracted.country) {
      const country = countries.find(c => 
        c.name.toLowerCase() === extracted.country.toLowerCase() ||
        c.name.toLowerCase().includes(extracted.country.toLowerCase())
      );
      if (country) { update('last_education_country', country.id); academicFields++; }
    }
    if (academicFields > 0) {
      filledSections.push(`Academic (${academicFields})`);
      totalFieldsFilled += academicFields;
    }

    // === ENGLISH TEST ===
    let testFields = 0;
    if (extracted.english_test_type) {
      // r60 — added oet + languagecert spelling variants. Keys are matched
      // case-insensitively against the AI-extracted test_type string.
      const testTypeMap = {
        'ielts': 'ielts', 'toefl': 'toefl', 'pte': 'pte', 'pearson': 'pte',
        'duolingo': 'duolingo', 'cambridge': 'cambridge',
        'oxford': 'oxford', 'ellt': 'oxford',
        'languagecert': 'langcert', 'language cert': 'langcert', 'langcert': 'langcert',
        'oet': 'oet', 'occupational english': 'oet',
      };
      const testType = Object.entries(testTypeMap).find(([key]) =>
        extracted.english_test_type.toLowerCase().includes(key)
      );
      if (testType) { update('english_test_type', testType[1]); testFields++; }
    }
    if (extracted.english_test_score) { update('english_test_score', extracted.english_test_score); testFields++; }
    if (extracted.english_test_date) { update('english_test_date', extracted.english_test_date); testFields++; }
    if (extracted.english_listening) { update('english_listening', extracted.english_listening); testFields++; }
    if (extracted.english_reading) { update('english_reading', extracted.english_reading); testFields++; }
    if (extracted.english_writing) { update('english_writing', extracted.english_writing); testFields++; }
    if (extracted.english_speaking) { update('english_speaking', extracted.english_speaking); testFields++; }
    if (testFields > 0) {
      filledSections.push(`English Test (${testFields})`);
      totalFieldsFilled += testFields;
    }

    // Return summary for toast message
    return { totalFieldsFilled, filledSections };
  };

  // Filter documents that can be scanned (images and PDFs)
  const scannableDocuments = documents.filter(doc => {
    const filename = (doc.filename || doc.file_name || '').toLowerCase();
    const fileType = (doc.file_type || '').toLowerCase();
    return filename.endsWith('.pdf') || filename.endsWith('.jpg') || filename.endsWith('.jpeg') || 
           filename.endsWith('.png') || fileType.includes('pdf') || fileType.includes('image');
  });

  // Section Scan Button component
  const SectionScanButton = ({ section }) => (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => openScanDialog(section)}
      disabled={readOnly || !api || documentTypes.length === 0}
      className="gap-1.5 h-7 text-xs"
      data-testid={`scan-${section}-btn`}
    >
      <ScanLine className="w-3.5 h-3.5" />
      Scan & Fill
      <CreditCostBadge feature={CREDIT_FEATURES.PROFILE_EXTRACTION} />
    </Button>
  );

  return (
    <div className="space-y-6">
      {/* Scan Dialog */}
      <Dialog open={scanDialogOpen} onOpenChange={setScanDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-primary" />
              Scan for {activeSection && sectionConfig[activeSection]?.title}
            </DialogTitle>
            <DialogDescription>
              {activeSection && sectionConfig[activeSection]?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Document Type Selection - from Documents Tab */}
            <div className="space-y-2">
              <Label>Save as Document Type</Label>
              <Select value={selectedDocTypeId} onValueChange={setSelectedDocTypeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(docType => (
                    <SelectItem key={docType.id} value={docType.id}>{docType.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Document will be saved under this heading in the Documents tab
              </p>
            </div>

            {/* Mode Toggle */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={scanMode === 'upload' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setScanMode('upload')}
                className="flex-1"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload New
              </Button>
              <Button
                type="button"
                variant={scanMode === 'select' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setScanMode('select')}
                className="flex-1"
                disabled={scannableDocuments.length === 0}
              >
                <FileText className="w-4 h-4 mr-2" />
                Select Existing ({scannableDocuments.length})
              </Button>
            </div>

            {/* Upload Mode */}
            {scanMode === 'upload' && (
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileScan(e.target.files?.[0])}
                  accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx"
                  className="hidden"
                />
                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm font-medium mb-1">Drop file here or click to browse</p>
                <p className="text-xs text-muted-foreground mb-3">Supports: Images, PDF, Word documents</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={scanning}
                >
                  {scanning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Browse Files
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Select Existing Mode */}
            {scanMode === 'select' && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {scannableDocuments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No scannable documents attached</p>
                ) : (
                  scannableDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      onClick={() => setSelectedExistingDoc(doc)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedExistingDoc?.id === doc.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedExistingDoc?.id === doc.id ? 'border-primary bg-primary' : 'border-muted-foreground'
                      }`}>
                        {selectedExistingDoc?.id === doc.id && <Check className="w-3 h-3 text-primary-foreground" />}
                      </div>
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.filename || doc.file_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.document_type_name || doc.document_type || 'Document'}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Credit Cost Info */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 text-sm">
              <CreditCard className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Cost: 1 credit per scan</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setScanDialogOpen(false)}>
              Cancel
            </Button>
            {scanMode === 'select' && (
              <Button
                onClick={handleExistingDocScan}
                disabled={!selectedExistingDoc || scanning}
              >
                {scanning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <ScanLine className="w-4 h-4 mr-2" />
                    Scan Selected
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Personal Information Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-primary">Personal Information</CardTitle>
            {readOnly && <CardDescription><Badge variant="outline" className="mt-1">View Only</Badge></CardDescription>}
          </div>
          {!readOnly && api && <SectionScanButton section="personal" />}
        </CardHeader>
        <CardContent className="space-y-4">
          {!isAgent && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Counselor</Label>
                  <Select value={data.counselor_id || 'none'} onValueChange={(v) => update('counselor_id', v === 'none' ? '' : v)} disabled={readOnly}>
                    <SelectTrigger><SelectValue placeholder="Select Counselor" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select Counselor</SelectItem>{renderUserOptions()}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Counseling Manager</Label>
                  <Select value={data.counseling_manager_id || 'none'} onValueChange={(v) => update('counseling_manager_id', v === 'none' ? '' : v)} disabled={readOnly}>
                    <SelectTrigger><SelectValue placeholder="Select Manager" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Select Manager</SelectItem>{renderUserOptions()}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Application Type</Label>
                  <Select value={data.application_type || 'none'} onValueChange={(v) => update('application_type', v === 'none' ? '' : v)} disabled={readOnly}>
                    <SelectTrigger><SelectValue placeholder="Select Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select Type</SelectItem>
                      {applicationTypes.length > 0 ? (
                        applicationTypes.map(type => (
                          <SelectItem key={type.id} value={type.name.toLowerCase().replace(/\s+/g, '_')}>
                            {type.name}
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="direct">Direct</SelectItem>
                          <SelectItem value="agent">Agent</SelectItem>
                          <SelectItem value="office">Office</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
            </>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Select value={data.title || 'none'} onValueChange={(v) => update('title', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="mr">Mr</SelectItem><SelectItem value="ms">Ms</SelectItem><SelectItem value="mrs">Mrs</SelectItem><SelectItem value="dr">Dr</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>First Name<span className="text-destructive">*</span></Label><Input value={data.first_name || ''} onChange={(e) => update('first_name', e.target.value)} data-testid="counselor-firstname" readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Last Name<span className="text-destructive">*</span></Label><Input value={data.last_name || ''} onChange={(e) => update('last_name', e.target.value)} data-testid="counselor-lastname" readOnly={readOnly} /></div>
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={data.gender || 'none'} onValueChange={(v) => update('gender', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2"><Label>Email<span className="text-destructive">*</span></Label><Input type="email" value={data.email || ''} onChange={(e) => update('email', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Telephone 1<span className="text-destructive">*</span></Label><PhoneInput value={data.telephone_1 || ''} onChange={(v) => update('telephone_1', v)} disabled={readOnly} nationality={countries.find(c => c.id === data.nationality)?.name} /></div>
            <div className="space-y-2"><Label>Telephone 2</Label><PhoneInput value={data.telephone_2 || ''} onChange={(v) => update('telephone_2', v)} disabled={readOnly} nationality={countries.find(c => c.id === data.nationality)?.name} /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Nationality</Label>{!readOnly && <QuickAddModal type="country" onSuccess={fetchMasterData} />}</div>
              <SearchableSelect 
                value={data.nationality || ''} 
                onChange={(v) => update('nationality', v)} 
                options={countries.map(c => ({ value: c.id, label: c.name }))}
                placeholder="Select Nationality"
                searchPlaceholder="Search countries..."
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={data.dob || ''} onChange={(e) => update('dob', e.target.value)} data-testid="counselor-dob" readOnly={readOnly} /></div>
          </div>
          <div className="space-y-2"><Label>Personal Information Notes</Label><Textarea value={data.personal_notes || ''} onChange={(e) => update('personal_notes', e.target.value)} rows={3} readOnly={readOnly} /></div>
        </CardContent>
      </Card>

      {/* ID Documents Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-primary">ID Documents</CardTitle>
          {!readOnly && api && <SectionScanButton section="id_documents" />}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2"><Label>NIC Number</Label><Input value={data.nic || ''} onChange={(e) => update('nic', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>NIC Issue Date</Label><Input type="date" value={data.nic_issue_date || ''} onChange={(e) => update('nic_issue_date', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>NIC Expiry Date</Label><Input type="date" value={data.nic_expiry_date || ''} onChange={(e) => update('nic_expiry_date', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Passport Number</Label><Input value={data.passport || ''} onChange={(e) => update('passport', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Passport Issue Date</Label><Input type="date" value={data.passport_issue_date || ''} onChange={(e) => update('passport_issue_date', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Passport Expiry Date</Label><Input type="date" value={data.passport_expiry_date || ''} onChange={(e) => update('passport_expiry_date', e.target.value)} readOnly={readOnly} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Address Details Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-primary">Address Details</CardTitle>
          {!readOnly && api && <SectionScanButton section="address" />}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-2"><Label>Address</Label><Input value={data.address || ''} onChange={(e) => update('address', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Postal Code</Label><Input value={data.postal_code || ''} onChange={(e) => update('postal_code', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Country Living In</Label>{!readOnly && <QuickAddModal type="country" onSuccess={fetchMasterData} />}</div>
              <SearchableSelect 
                value={data.country_living || ''} 
                onChange={(v) => update('country_living', v)} 
                options={countries.map(c => ({ value: c.id, label: c.name }))}
                placeholder="Select Country"
                searchPlaceholder="Search countries..."
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2"><Label>Province/State</Label><Input value={data.province || ''} onChange={(e) => update('province', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>City</Label><Input value={data.city || ''} onChange={(e) => update('city', e.target.value)} readOnly={readOnly} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Academic Details Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-primary">Academic Details</CardTitle>
          {!readOnly && api && <SectionScanButton section="academic" />}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Last Education Country</Label>{!readOnly && <QuickAddModal type="country" onSuccess={fetchMasterData} />}</div>
              <SearchableSelect 
                value={data.last_education_country || ''} 
                onChange={(v) => update('last_education_country', v)} 
                options={countries.map(c => ({ value: c.id, label: c.name }))}
                placeholder="Select Country"
                searchPlaceholder="Search countries..."
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2">
              <Label>Last Qualification</Label>
              <Select value={data.last_qualification || 'none'} onValueChange={(v) => update('last_qualification', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="high_school">High School</SelectItem><SelectItem value="diploma">Diploma</SelectItem><SelectItem value="bachelors_3">Bachelor 3 Years</SelectItem><SelectItem value="bachelors_4">Bachelor 4 Years</SelectItem><SelectItem value="masters">Masters</SelectItem><SelectItem value="phd">PhD</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Qualification Status</Label>
              <Select value={data.qualification_status || 'none'} onValueChange={(v) => update('qualification_status', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="incomplete">Incomplete</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Subject</Label><Input value={data.last_qualification_subject || ''} onChange={(e) => update('last_qualification_subject', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Faculty</Label><Input value={data.last_qualification_faculty || ''} onChange={(e) => update('last_qualification_faculty', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Grades/GPA</Label><Input value={data.last_qualification_grades || ''} onChange={(e) => update('last_qualification_grades', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Qualification Year</Label><Input value={data.last_qualification_year || ''} onChange={(e) => update('last_qualification_year', e.target.value)} placeholder="e.g., 2020" readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Last Institution Name</Label><Input value={data.last_institution || ''} onChange={(e) => update('last_institution', e.target.value)} readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Institution City</Label><Input value={data.last_institution_city || ''} onChange={(e) => update('last_institution_city', e.target.value)} readOnly={readOnly} /></div>
          </div>
        </CardContent>
      </Card>

      {/* English Test Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-primary">English Test</CardTitle>
          {!readOnly && api && <SectionScanButton section="english_test" />}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>English Test Type</Label>
              <Select value={data.english_test_type || 'none'} onValueChange={(v) => update('english_test_type', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select</SelectItem>
                  <SelectItem value="ielts">IELTS</SelectItem>
                  <SelectItem value="toefl">TOEFL</SelectItem>
                  <SelectItem value="pte">PTE</SelectItem>
                  <SelectItem value="duolingo">Duolingo</SelectItem>
                  <SelectItem value="cambridge">Cambridge</SelectItem>
                  <SelectItem value="oxford">Oxford</SelectItem>
                  <SelectItem value="langcert">Langcert</SelectItem>
                  <SelectItem value="none_required">None Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Overall Score</Label><Input value={data.english_test_score || ''} onChange={(e) => update('english_test_score', e.target.value)} placeholder="e.g., 7.5" readOnly={readOnly} /></div>
            <div className="space-y-2"><Label>Test Date</Label><Input type="date" value={data.english_test_date || ''} onChange={(e) => update('english_test_date', e.target.value)} readOnly={readOnly} /></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2"><Label className="text-sm">Listening</Label><Input value={data.english_listening || ''} onChange={(e) => update('english_listening', e.target.value)} placeholder="Score" readOnly={readOnly} /></div>
            <div className="space-y-2"><Label className="text-sm">Reading</Label><Input value={data.english_reading || ''} onChange={(e) => update('english_reading', e.target.value)} placeholder="Score" readOnly={readOnly} /></div>
            <div className="space-y-2"><Label className="text-sm">Writing</Label><Input value={data.english_writing || ''} onChange={(e) => update('english_writing', e.target.value)} placeholder="Score" readOnly={readOnly} /></div>
            <div className="space-y-2"><Label className="text-sm">Speaking</Label><Input value={data.english_speaking || ''} onChange={(e) => update('english_speaking', e.target.value)} placeholder="Score" readOnly={readOnly} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Application Processing Section */}
      <Card>
        <CardHeader><CardTitle className="text-primary">Application Processing</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Lead Status</Label>
              <Select value={data.lead_status || 'none'} onValueChange={(v) => update('lead_status', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select</SelectItem>{renderLeadStatusOptions()}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Processing Status</Label>
              <Select value={data.processing_status || 'none'} onValueChange={(v) => update('processing_status', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="new">New</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="documents_pending">Documents Pending</SelectItem><SelectItem value="ready_for_submission">Ready for Submission</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lead Initiated By</Label>
              <Select value={data.lead_initiated_by || 'none'} onValueChange={(v) => update('lead_initiated_by', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Select</SelectItem><SelectItem value="direct">Direct</SelectItem><SelectItem value="agent">Agent</SelectItem><SelectItem value="referral">Referral</SelectItem><SelectItem value="marketing">Marketing</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CounselorTab;
